/**
 * EST3 Client BOQ import column presets — KEEP_SEPARATE from WBS / Manufacturing BOQ.
 * Keep in sync with backend `boq-import-mapping.ts`.
 */

export type Est3BoqImportPreset = 'auto' | 'est3' | 'cesmm' | 'generic_sop' | 'custom';

export type BoqImportColumnMap = {
  itemNumber?: string[];
  description: string[];
  parentItemNumber?: string[];
  quantity?: string[];
  uom?: string[];
  unitRate?: string[];
  notes?: string[];
  amount?: string[];
};

export const BOQ_IMPORT_PRESET_LABELS: Record<Est3BoqImportPreset, string> = {
  auto: 'Auto-detect',
  est3: 'EST3 template',
  cesmm: 'CESMM-like',
  generic_sop: 'Generic schedule of prices',
  custom: 'Custom mapping',
};

export const BOQ_IMPORT_PRESET_HINTS: Record<Est3BoqImportPreset, string> = {
  auto: 'Detects EST3, CESMM-like, or generic SoP columns from the header row.',
  est3: 'Item Number, Description, Quantity, UOM, Unit Rate, Notes — hierarchy from Item Numbers.',
  cesmm: 'Item/Ref, Description, Qty, Unit, Rate (Amount ignored) — hierarchy from Item/Ref.',
  generic_sop: 'No., Description/Particulars, Quantity, Unit, Rate/Price — hierarchy from No.',
  custom: 'Map each target field to a spreadsheet column manually.',
};

export const BOQ_IMPORT_PRESETS: Record<Exclude<Est3BoqImportPreset, 'auto'>, BoqImportColumnMap> = {
  est3: {
    itemNumber: ['item number', 'item #', 'item_number', 'item'],
    description: ['description', 'desc'],
    parentItemNumber: ['parent item number', 'parent item', 'parent_item_number', 'parent'],
    quantity: ['quantity', 'qty'],
    uom: ['uom', 'unit'],
    unitRate: ['unit rate', 'unit_rate', 'rate'],
    notes: ['notes', 'remark', 'remarks'],
    amount: ['amount', 'total', 'line amount', 'line total', 'total amount'],
  },
  cesmm: {
    itemNumber: ['item', 'ref', 'item ref', 'item no', 'item no.', 'reference'],
    description: ['description', 'desc', 'work description'],
    parentItemNumber: ['parent item', 'parent', 'parent ref'],
    quantity: ['qty', 'quantity'],
    uom: ['unit', 'uom'],
    unitRate: ['rate', 'unit rate', 'price'],
    notes: ['notes', 'remarks', 'remark'],
    amount: ['amount', 'total', 'extended', 'total amount'],
  },
  generic_sop: {
    itemNumber: ['no.', 'no', 'item no', 'item no.', 'item #', 'bill item', 's.no', 'sr no'],
    description: ['bill', 'description', 'bill description', 'particulars', 'item description'],
    parentItemNumber: ['parent', 'parent item', 'parent no'],
    quantity: ['quantity', 'qty'],
    uom: ['unit', 'uom', 'unit of measure'],
    unitRate: ['rate', 'price', 'unit rate', 'unit price'],
    notes: ['remarks', 'remark', 'notes'],
    amount: ['amount', 'total', 'value', 'total amount'],
  },
  custom: {
    description: [],
  },
};

export type BoqImportColumnKey =
  | 'itemNumber'
  | 'description'
  | 'parentItemNumber'
  | 'quantity'
  | 'uom'
  | 'unitRate'
  | 'notes'
  | 'amount';

export type ResolvedBoqImportColumns = {
  preset: Exclude<Est3BoqImportPreset, 'auto'>;
  itemNumber: number;
  description: number;
  parentItemNumber: number;
  quantity: number;
  uom: number;
  unitRate: number;
  notes: number;
  amount: number;
  deriveParentFromItemNumber: boolean;
};

export type BoqImportMappingPayload = {
  preset?: Est3BoqImportPreset;
  columns?: Partial<Record<BoqImportColumnKey, number>>;
  deriveParentFromItemNumber?: boolean;
  headerRowIndex?: number;
  /** When set, only these sheet names are imported. Default = all sheets. */
  sheetNames?: string[];
};

export const BOQ_IMPORT_TARGET_FIELDS: Array<{
  key: BoqImportColumnKey;
  label: string;
  required?: boolean;
}> = [
  { key: 'itemNumber', label: 'Item Number' },
  { key: 'description', label: 'Description', required: true },
  { key: 'quantity', label: 'Quantity' },
  { key: 'uom', label: 'UOM' },
  { key: 'unitRate', label: 'Unit Rate' },
  { key: 'notes', label: 'Notes' },
  { key: 'amount', label: 'Amount (check only)' },
];

export function normBoqImportHeader(h: string): string {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.#:]+$/g, '')
    .trim();
}

export function headerIndex(headerRow: string[], names: string[]): number {
  for (const n of names) {
    const i = headerRow.findIndex((h) => normBoqImportHeader(h) === normBoqImportHeader(n));
    if (i >= 0) return i;
  }
  return -1;
}

function scorePreset(headers: string[], map: BoqImportColumnMap): number {
  let score = 0;
  if (headerIndex(headers, map.description) >= 0) score += 3;
  if (map.itemNumber && headerIndex(headers, map.itemNumber) >= 0) score += 2;
  if (map.quantity && headerIndex(headers, map.quantity) >= 0) score += 2;
  if (map.unitRate && headerIndex(headers, map.unitRate) >= 0) score += 2;
  if (map.uom && headerIndex(headers, map.uom) >= 0) score += 1;
  if (map.parentItemNumber && headerIndex(headers, map.parentItemNumber) >= 0) score += 1;
  if (map.notes && headerIndex(headers, map.notes) >= 0) score += 1;
  return score;
}

export function detectBoqImportPreset(headers: string[]): Exclude<Est3BoqImportPreset, 'auto'> {
  const scores: Array<{ preset: Exclude<Est3BoqImportPreset, 'auto' | 'custom'>; score: number }> = [
    { preset: 'est3', score: scorePreset(headers, BOQ_IMPORT_PRESETS.est3) },
    { preset: 'cesmm', score: scorePreset(headers, BOQ_IMPORT_PRESETS.cesmm) },
    { preset: 'generic_sop', score: scorePreset(headers, BOQ_IMPORT_PRESETS.generic_sop) },
  ];
  scores.sort((a, b) => b.score - a.score);
  if (scores[0].score > 0) return scores[0].preset;
  return 'custom';
}

export function resolveBoqImportColumns(
  headers: string[],
  payload: BoqImportMappingPayload = {}
): ResolvedBoqImportColumns {
  const presetIn = payload.preset || 'auto';
  const resolvedPreset =
    presetIn === 'auto' ? detectBoqImportPreset(headers) : presetIn === 'custom' ? 'custom' : presetIn;

  const map = BOQ_IMPORT_PRESETS[resolvedPreset];
  const fromAliases = {
    itemNumber: map.itemNumber ? headerIndex(headers, map.itemNumber) : -1,
    description: map.description.length ? headerIndex(headers, map.description) : -1,
    parentItemNumber: map.parentItemNumber ? headerIndex(headers, map.parentItemNumber) : -1,
    quantity: map.quantity ? headerIndex(headers, map.quantity) : -1,
    uom: map.uom ? headerIndex(headers, map.uom) : -1,
    unitRate: map.unitRate ? headerIndex(headers, map.unitRate) : -1,
    notes: map.notes ? headerIndex(headers, map.notes) : -1,
    amount: map.amount ? headerIndex(headers, map.amount) : -1,
  };

  const overrides = payload.columns || {};
  const pick = (key: BoqImportColumnKey): number => {
    const o = overrides[key];
    if (typeof o === 'number' && Number.isFinite(o)) return Math.trunc(o);
    return fromAliases[key];
  };

  const derive = payload.deriveParentFromItemNumber !== false;

  return {
    preset: resolvedPreset,
    itemNumber: pick('itemNumber'),
    description: pick('description'),
    parentItemNumber: pick('parentItemNumber'),
    quantity: pick('quantity'),
    uom: pick('uom'),
    unitRate: pick('unitRate'),
    notes: pick('notes'),
    amount: pick('amount'),
    deriveParentFromItemNumber: derive,
  };
}

export function parentItemFromOutline(itemNumber: string): string | undefined {
  const trimmed = itemNumber.trim();
  if (!trimmed) return undefined;
  const parts = trimmed.split('.').filter(Boolean);
  if (parts.length <= 1) return undefined;
  parts.pop();
  return parts.join('.');
}

/**
 * Outline depth for construction-style item codes:
 * DIV 16 → 0, E3 → 1, E3.1 → 2, E3.1.1 → 3.
 */
export function boqItemOutlineDepth(itemNumber: string): number {
  const t = itemNumber.trim();
  if (!t) return -1;
  if (/^(div|division|bill|section)\b/i.test(t)) return 0;
  const dots = (t.match(/\./g) || []).length;
  return 1 + dots;
}

/**
 * Resolve parent item from Excel item numbers only.
 * Tries outline parents, then section convention ("1.1" → "1.0" / "1") against known items in the file.
 */
export function resolveParentItemNumber(
  itemNumber: string | undefined,
  knownItemNumbers: Iterable<string>
): string | undefined {
  if (!itemNumber?.trim()) return undefined;
  const item = itemNumber.trim();
  const known = new Set(
    [...knownItemNumbers].map((k) => String(k || '').trim()).filter(Boolean)
  );
  known.delete(item);

  const tryMatch = (candidate: string | undefined): string | undefined => {
    if (!candidate) return undefined;
    if (known.has(candidate)) return candidate;
    const withZero = `${candidate}.0`;
    if (known.has(withZero)) return withZero;
    return undefined;
  };

  let outline = parentItemFromOutline(item);
  while (outline) {
    const hit = tryMatch(outline);
    if (hit) return hit;
    outline = parentItemFromOutline(outline);
  }

  const parts = item.split('.').filter(Boolean);
  if (parts.length >= 2 && parts[parts.length - 1] !== '0') {
    const section = `${parts.slice(0, -1).join('.')}.0`;
    if (section !== item && known.has(section)) return section;
  }

  return undefined;
}

export type BoqHierarchyRow = {
  itemNumber?: string;
  parentItemNumber?: string;
  description: string;
  notes?: string;
  status?: string;
  errorMessage?: string;
  amountText?: string;
};

/**
 * Assign parents from Excel structure:
 * - coded rows use outline depth stack (DIV → E3 → E3.1)
 * - blank Item No. rows nest under the last coded item
 * - page "(Continued)" headers are marked skipped
 */
export function applyExcelBoqHierarchy<T extends BoqHierarchyRow>(rows: T[]): T[] {
  const stack: Array<{ itemNumber: string; depth: number }> = [];
  const out: T[] = [];

  for (const r of rows) {
    const item = (r.itemNumber || '').trim() || undefined;
    const amountText = String(r.amountText || '').trim();
    const desc = String(r.description || '');
    const notes = String(r.notes || '');
    const isContinuation =
      /^\(?\s*continued\s*\)?$/i.test(amountText) ||
      /\(continued\)/i.test(desc) ||
      /\(continued\)/i.test(notes);

    if (item && isContinuation) {
      out.push({
        ...r,
        itemNumber: item,
        parentItemNumber: undefined,
        status: 'error',
        errorMessage: 'Page continuation header — skipped',
      });
      continue;
    }

    if (r.parentItemNumber?.trim()) {
      if (item) {
        const d = boqItemOutlineDepth(item);
        while (stack.length && stack[stack.length - 1].depth >= d) stack.pop();
        stack.push({ itemNumber: item, depth: d });
      }
      out.push({ ...r, itemNumber: item, parentItemNumber: r.parentItemNumber.trim() });
      continue;
    }

    if (item) {
      const d = boqItemOutlineDepth(item);
      while (stack.length && stack[stack.length - 1].depth >= d) stack.pop();
      const parent = stack.length ? stack[stack.length - 1].itemNumber : undefined;
      out.push({ ...r, itemNumber: item, parentItemNumber: parent });
      stack.push({ itemNumber: item, depth: d });
    } else {
      const parent = stack.length ? stack[stack.length - 1].itemNumber : undefined;
      out.push({ ...r, itemNumber: undefined, parentItemNumber: parent });
    }
  }
  return out;
}

export function columnsPayloadFromResolved(
  cols: ResolvedBoqImportColumns
): Partial<Record<BoqImportColumnKey, number>> {
  return {
    itemNumber: cols.itemNumber,
    description: cols.description,
    parentItemNumber: cols.parentItemNumber,
    quantity: cols.quantity,
    uom: cols.uom,
    unitRate: cols.unitRate,
    notes: cols.notes,
    amount: cols.amount,
  };
}
